import { Box, Button, Container } from '@mui/material'
import { useOperationsStore } from '@operations/AppProvider'
import { useTranslate } from '@tolgee/react'
import { Breadcrumbs, Header, useMinimalsTheme } from '@tom-ui/ui'
import { PATHS } from '@tom-ui/utils'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { BreadcrumpItem } from 'modules/ui/src/theme/components/breadcrumbs/Breadcrumb'
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router'
import { HatchCoverMoveItemForm } from './components/organisms/HatchCoverMoveItemForm'

export const HatchCoverMoveItemPage = observer(() => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()
  const { hatchCoverMoveStore, carrierVisitStore, appStore } = useOperationsStore()
  const { id, hatchcoverid } = useParams<{ id: string; hatchcoverid: string }>()
  const navigate = useNavigate()
  const visitId = Number(id)
  const hatchCoverId = Number(hatchcoverid)

  useEffect(() => {
    async function fetchVisit() {
      carrierVisitStore.loadOne(visitId)
    }
    if (!carrierVisitStore.items.find(v => v.id === visitId)) fetchVisit()
  }, [carrierVisitStore, visitId])

  useEffect(() => {
    async function fetchHatchCovers() {
      await hatchCoverMoveStore.fetchDataByVisitId(visitId)
    }
    fetchHatchCovers()
  }, [hatchCoverMoveStore, visitId])

  const vesselVisit = computed(() => carrierVisitStore.items.find(v => v.id === visitId)).get()
  const hatchCoverMove = computed(() =>
    hatchCoverMoveStore.items.find(h => h.id === hatchCoverId),
  ).get()

  const craneItems = computed(() => hatchCoverMoveStore.cranes).get()

  const formId = 'hatchCoverMoveItemForm'

  const breadCrumbs: BreadcrumpItem[] = [
    {
      label: vesselVisit?.carrierName ?? '',
      onClick: () => navigate(`${PATHS.visits.vesselVisits.url}/${vesselVisit?.id}/dashboard`),
    },
    {
      label: t('hatchCoverMoves', 'Hatch cover moves'),
      onClick: () =>
        navigate(
          `${PATHS.visits.vesselVisits.url}/${vesselVisit?.id}/${PATHS.visits.vesselVisits.hatchCovers.url}`,
        ),
    },
    {
      label: hatchCoverId ? t('edit', 'Edit') : t('create', 'Create'),
    },
  ]

  if (vesselVisit?.id && hatchCoverMoveStore.cranes.length === 0) {
    navigate(
      `${PATHS.visits.vesselVisits.url}/${vesselVisit?.id}/${PATHS.visits.vesselVisits.hatchCovers.url}`,
    )
  }

  return (
    <Box
      sx={{
        bgcolor: theme.palette.grey[100],
        minHeight: '100%',
      }}
    >
      <Breadcrumbs items={breadCrumbs} />
      <Header
        title={t('upsertHatchCoverMove', '{action} hatch cover move', {
          action: hatchcoverid ? t('create', 'Create') : t('edit', 'Edit'),
        })}
        rightRenderOption={
          <>
            <Button
              variant='text'
              color='inherit'
              data-cy='cancel-hatch-cover-move-item-btn'
              onClick={() =>
                navigate(
                  `${PATHS.visits.vesselVisits.url}/${vesselVisit?.id}/${PATHS.visits.vesselVisits.hatchCovers.url}`,
                )
              }
            >
              {t('cancel', 'Cancel')}
            </Button>
            <Button
              variant='contained'
              color='primary'
              type='submit'
              form={formId}
              data-cy='confirm-hatch-cover-move-item-btn'
            >
              {t('submit', 'Submit')}
            </Button>
          </>
        }
      />
      <Container sx={{ paddingTop: theme.customSpacing.m }}>
        <HatchCoverMoveItemForm
          formId={formId}
          vesselVisitId={visitId}
          craneItems={craneItems}
          onSubmit={async h => {
            await hatchCoverMoveStore.onSubmit(h)
            navigate(
              `${PATHS.visits.vesselVisits.url}/${vesselVisit?.id}/${PATHS.visits.vesselVisits.hatchCovers.url}`,
            )
            appStore.setShowAlert('success', t('hatchCoverMoveSaved', 'Hatch cover move saved'))
          }}
          hatchCoverMove={hatchCoverMove}
        />
      </Container>
    </Box>
  )
})
